import type { Locale } from "./i18n";

interface DirectServiceContent {
  readonly navDirect: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly primaryAction: string;
  readonly secondaryAction: string;
  readonly proof: readonly [string, string, string];
  readonly visualLabel: string;
  readonly visualStatus: string;
  readonly visualTypes: readonly [string, string, string, string];
  readonly visualProjects: readonly [string, string, string, string];
  readonly visualBudget: string;
  readonly visualSchedule: string;
  readonly visualManager: string;
  readonly sectionEyebrow: string;
  readonly sectionTitle: string;
  readonly sectionBody: string;
  readonly directBadge: string;
  readonly directTitle: string;
  readonly directBody: string;
  readonly directPoints: readonly [string, string, string, string];
  readonly partnerBadge: string;
  readonly partnerTitle: string;
  readonly partnerBody: string;
  readonly partnerPoints: readonly [string, string, string, string];
  readonly responsibilityNote: string;
  readonly processEyebrow: string;
  readonly processTitle: string;
  readonly processBody: string;
  readonly processTitles: readonly [string, string, string, string, string, string];
  readonly processDescriptions: readonly [string, string, string, string, string, string];
  readonly estimateNotice: string;
  readonly partnerSectionLabel: string;
  readonly partnerSectionTitle: string;
  readonly partnerSectionBody: string;
}

const english: DirectServiceContent = {
  navDirect: "Interior services",
  eyebrow: "INTERIOR DESIGN & PROJECT MANAGEMENT IN THAILAND",
  title: "Design, build and aftercare\nmanaged in one place",
  body: "Decobaan coordinates the brief, site survey, BOQ, project team, construction records, inspection and aftercare in one project.",
  primaryAction: "Request a consultation",
  secondaryAction: "Prepare with a 3D plan",
  proof: ["Clear project responsibility", "Versioned scope and BOQ", "Progress, inspection and defect records"],
  visualLabel: "PROJECT OVERVIEW",
  visualStatus: "Consultation",
  visualTypes: ["Commercial · Priority 1", "Office · Priority 2", "Condo · Priority 3", "Townhouse · Priority 4"],
  visualProjects: ["Retail shop fit-out · Bangkok", "Office renovation · Bangkok", "Condo renovation · Bangkok", "Townhouse renovation · Bangkok"],
  visualBudget: "Budget",
  visualSchedule: "Schedule",
  visualManager: "Project lead",
  sectionEyebrow: "ONE CONNECTED SERVICE",
  sectionTitle: "A clear path from brief to handover",
  sectionBody: "Design decisions, scope, schedule, site evidence and approvals stay connected in one project record.",
  directBadge: "DESIGN & PLANNING",
  directTitle: "Design, survey and BOQ",
  directBody: "Start with the real site conditions and turn the brief into a reviewable design, scope and quotation.",
  directPoints: ["Brief and site survey", "Design and material direction", "Versioned scope and BOQ", "Schedule and quotation review"],
  partnerBadge: "BUILD & AFTERCARE",
  partnerTitle: "Construction, inspection and aftercare",
  partnerBody: "Manage site progress, written changes, inspection and follow-up work without losing the project history.",
  partnerPoints: ["Project team assignment", "Schedule and site evidence", "Written variation approvals", "Inspection and aftercare records"],
  responsibilityNote: "The assigned team and contracting party are identified clearly at the written quotation and contract stage after the site review. Online estimates remain planning ranges only.",
  processEyebrow: "PROJECT JOURNEY",
  processTitle: "One project record from request to aftercare",
  processBody: "Every decision is attached to the project instead of being lost across calls and chat messages.",
  processTitles: ["Request", "Site survey", "Design & BOQ", "Contract", "Build & approve", "Inspect & aftercare"],
  processDescriptions: ["Share the space, desired scope, location and planning budget.", "The team verifies dimensions, site conditions and building rules.", "Review the design, materials, quantities, exclusions, price and schedule.", "Accept the agreed scope and quotation version in writing.", "Track milestones and approve written variations before extra work begins.", "Complete the checklist, corrections, handover and defect records."],
  estimateNotice: "Planning estimate · not a contract price before site survey",
  partnerSectionLabel: "PROJECT PROFESSIONALS",
  partnerSectionTitle: "Specialists matched to the needs of each project",
  partnerSectionBody: "Only professionals with reviewed profiles and active operating status appear here. Final roles are confirmed during consultation."
};

export const directServiceCopy: Readonly<Record<Locale, DirectServiceContent>> = {
  th: {
    navDirect: "บริการอินทีเรียร์",
    eyebrow: "ออกแบบและบริหารโครงการอินทีเรียร์ในประเทศไทย",
    title: "ดูแลตั้งแต่ดีไซน์และก่อสร้าง\nถึงบริการหลังส่งมอบในที่เดียว",
    body: "Decobaan จัดการบรีฟ สำรวจหน้างาน BOQ ทีมโครงการ บันทึกงานก่อสร้าง การตรวจรับ และงานแก้ไขไว้ในโปรเจกต์เดียว",
    primaryAction: "ขอคำปรึกษาอินทีเรียร์",
    secondaryAction: "เตรียมแบบด้วย 3D",
    proof: ["กำหนดผู้รับผิดชอบโครงการชัดเจน", "ขอบเขตและ BOQ แยกตามเวอร์ชัน", "บันทึกความคืบหน้า ตรวจรับ และงานแก้ไข"],
    visualLabel: "ภาพรวมโปรเจกต์",
    visualStatus: "เตรียมคำปรึกษา",
    visualTypes: ["ร้านค้า · ลำดับ 1", "สำนักงาน · ลำดับ 2", "คอนโด · ลำดับ 3", "ทาวน์เฮาส์ · ลำดับ 4"],
    visualProjects: ["ตกแต่งร้านค้า · กรุงเทพฯ", "รีโนเวทสำนักงาน · กรุงเทพฯ", "รีโนเวทคอนโด · กรุงเทพฯ", "รีโนเวททาวน์เฮาส์ · กรุงเทพฯ"],
    visualBudget: "งบประมาณ",
    visualSchedule: "กำหนดการ",
    visualManager: "ผู้ดูแลโปรเจกต์",
    sectionEyebrow: "บริการที่เชื่อมต่อในโปรเจกต์เดียว",
    sectionTitle: "เส้นทางชัดเจนตั้งแต่บรีฟจนถึงส่งมอบ",
    sectionBody: "ดีไซน์ ขอบเขต กำหนดการ หลักฐานหน้างาน และการอนุมัติถูกจัดเก็บต่อเนื่องในโปรเจกต์เดียว",
    directBadge: "ดีไซน์และวางแผน",
    directTitle: "ดีไซน์ สำรวจหน้างาน และ BOQ",
    directBody: "เริ่มจากสภาพหน้างานจริง แล้วพัฒนาบรีฟเป็นดีไซน์ ขอบเขต และใบเสนอราคาที่ตรวจสอบได้",
    directPoints: ["รับบรีฟและสำรวจหน้างาน", "แนวทางดีไซน์และวัสดุ", "ขอบเขตและ BOQ แยกเวอร์ชัน", "ตรวจสอบกำหนดการและใบเสนอราคา"],
    partnerBadge: "ก่อสร้างและบริการหลังส่งมอบ",
    partnerTitle: "ก่อสร้าง ตรวจรับ และแก้ไขงาน",
    partnerBody: "ติดตามความคืบหน้า งานเปลี่ยนแปลง การตรวจรับ และงานติดตามผลโดยไม่สูญเสียประวัติโครงการ",
    partnerPoints: ["จัดทีมตามโครงการ", "กำหนดการและหลักฐานหน้างาน", "อนุมัติงานเปลี่ยนแปลงเป็นลายลักษณ์อักษร", "บันทึกตรวจรับและบริการหลังส่งมอบ"],
    responsibilityNote: "ทีมที่รับผิดชอบและคู่สัญญาจะแจ้งอย่างชัดเจนในขั้นตอนใบเสนอราคาและสัญญาหลังสำรวจหน้างาน ราคาประเมินออนไลน์เป็นข้อมูลเพื่อวางแผนเท่านั้น",
    processEyebrow: "ขั้นตอนโครงการ",
    processTitle: "ข้อมูลเดียวตั้งแต่คำขอถึงบริการหลังส่งมอบ",
    processBody: "ทุกการตัดสินใจผูกกับโปรเจกต์ ไม่กระจัดกระจายในโทรศัพท์หรือแชต",
    processTitles: ["ส่งคำขอ", "สำรวจหน้างาน", "ดีไซน์และ BOQ", "ทำสัญญา", "ก่อสร้างและอนุมัติ", "ตรวจรับและแก้ไข"],
    processDescriptions: ["แจ้งประเภทพื้นที่ ขอบเขต ทำเล และงบประมาณสำหรับวางแผน", "ทีมตรวจสอบขนาด สภาพหน้างาน และกฎของอาคาร", "ตรวจดีไซน์ วัสดุ ปริมาณ งานที่ไม่รวม ราคา และกำหนดการ", "ยอมรับขอบเขตและเวอร์ชันใบเสนอราคาที่ตกลงเป็นลายลักษณ์อักษร", "ติดตามแต่ละงวดและอนุมัติงานเปลี่ยนแปลงก่อนเริ่มงานเพิ่ม", "ตรวจตามเช็กลิสต์ แก้ไข ส่งมอบ และบันทึกงานหลังส่งมอบ"],
    estimateNotice: "ราคาสำหรับวางแผน · ไม่ใช่ราคาสัญญาก่อนสำรวจหน้างาน",
    partnerSectionLabel: "ผู้เชี่ยวชาญสำหรับโครงการ",
    partnerSectionTitle: "เลือกผู้เชี่ยวชาญให้เหมาะกับความต้องการของแต่ละงาน",
    partnerSectionBody: "แสดงเฉพาะผู้เชี่ยวชาญที่ผ่านการตรวจสอบโปรไฟล์และมีสถานะให้บริการ โดยยืนยันบทบาทจริงในขั้นตอนปรึกษา"
  },
  ko: {
    navDirect: "인테리어 서비스",
    eyebrow: "태국 인테리어 설계·시공 관리",
    title: "디자인부터 하자관리까지\n한 번에 관리합니다",
    body: "데코반이 상담, 실측, BOQ, 프로젝트 팀 구성, 시공 기록, 검수와 하자관리를 하나의 프로젝트에서 관리합니다.",
    primaryAction: "인테리어 상담 요청",
    secondaryAction: "3D 도면으로 준비하기",
    proof: ["프로젝트 책임과 연락 창구 명확화", "범위·BOQ 버전 관리", "공정·검수·하자 기록"],
    visualLabel: "프로젝트 개요",
    visualStatus: "상담 준비",
    visualTypes: ["상가 · 1순위", "오피스 · 2순위", "콘도 · 3순위", "타운하우스 · 4순위"],
    visualProjects: ["리테일 매장 시공 · 방콕", "오피스 리노베이션 · 방콕", "콘도 리노베이션 · 방콕", "타운하우스 리노베이션 · 방콕"],
    visualBudget: "예산",
    visualSchedule: "일정",
    visualManager: "프로젝트 책임자",
    sectionEyebrow: "하나로 이어지는 인테리어 서비스",
    sectionTitle: "상담부터 인도까지 흐름이 끊기지 않게",
    sectionBody: "디자인 결정, 공사 범위, 일정, 현장 증빙과 승인을 하나의 프로젝트 기록으로 연결합니다.",
    directBadge: "디자인·기획",
    directTitle: "디자인·실측·BOQ",
    directBody: "실제 현장 조건을 먼저 확인하고 요청사항을 검토 가능한 디자인, 공사 범위와 견적으로 구체화합니다.",
    directPoints: ["상담과 현장 실측", "디자인과 자재 방향", "범위·BOQ 버전 관리", "일정과 견적 검토"],
    partnerBadge: "시공·하자관리",
    partnerTitle: "시공·검수·하자관리",
    partnerBody: "현장 진행, 변경 승인, 검수와 후속 조치까지 프로젝트 이력을 잃지 않고 관리합니다.",
    partnerPoints: ["프로젝트별 담당 팀 구성", "일정과 현장 증빙", "서면 변경공사 승인", "검수와 하자관리 기록"],
    responsibilityNote: "실제 담당 팀과 계약 주체는 현장 확인 후 서면 견적·계약 단계에서 명확히 안내합니다. 온라인 예상 금액은 계획용 범위입니다.",
    processEyebrow: "프로젝트 진행 과정",
    processTitle: "요청부터 하자관리까지 하나의 프로젝트 기록",
    processBody: "전화와 메신저에 흩어지지 않도록 모든 결정과 증빙을 프로젝트에 연결합니다.",
    processTitles: ["요청", "현장 실측", "디자인·BOQ", "계약", "시공·승인", "검수·하자관리"],
    processDescriptions: ["공간, 희망 범위, 지역과 계획 예산을 알려주세요.", "실제 치수, 현장 조건과 건물 관리 규칙을 확인합니다.", "디자인, 자재, 수량, 제외 범위, 가격과 일정을 검토합니다.", "합의된 범위와 견적 버전을 서면으로 수락합니다.", "공정을 확인하고 추가 작업은 변경 승인 후 진행합니다.", "체크리스트 검수, 보완, 인도와 하자 이력을 관리합니다."],
    estimateNotice: "계획용 예상 견적 · 현장 실측 전 계약 금액이 아닙니다",
    partnerSectionLabel: "프로젝트 전문가",
    partnerSectionTitle: "프로젝트에 필요한 분야별 전문가",
    partnerSectionBody: "프로필과 운영 상태를 확인한 전문가만 공개하며 실제 역할은 상담 과정에서 확정합니다."
  },
  en: english,
  es: english,
  zh: english,
  ja: english
};
